/**
 * VisibilityPromptModal
 *
 * Modal dialog shown before saving a new template to prompt
 * the Super Admin to configure visibility settings.
 *
 * This ensures that every template has deliberate visibility settings
 * rather than relying on defaults.
 */
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@respectly/ui';
import { Eye, Loader2, AlertCircle } from 'lucide-react';
import { VisibilityControlsPanel, type VisibilityData } from './VisibilityControlsPanel';

export interface VisibilityPromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateName: string;
  templateType: 'form' | 'document';
  /** Initial visibility settings (if editing existing template) */
  initialVisibility?: VisibilityData;
  /** Called when user confirms visibility settings */
  onConfirm: (data: VisibilityData) => void;
  /** Called when user cancels */
  onCancel?: () => void;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Error message to display */
  error?: string | null;
}

export function VisibilityPromptModal({
  open,
  onOpenChange,
  templateName,
  templateType,
  initialVisibility,
  onConfirm,
  onCancel,
  isSaving = false,
  error = null,
}: VisibilityPromptModalProps) {
  // Local state for visibility settings
  const [visibility, setVisibility] = useState<VisibilityData['visibility']>('all');
  const [visibleToPlans, setVisibleToPlans] = useState<string[]>([]);
  const [visibleToTenants, setVisibleToTenants] = useState<string[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      if (initialVisibility) {
        setVisibility(initialVisibility.visibility);
        setVisibleToPlans(initialVisibility.visibleToPlans);
        setVisibleToTenants(initialVisibility.visibleToTenants);
      } else {
        // Default to 'all' for new templates
        setVisibility('all');
        setVisibleToPlans([]);
        setVisibleToTenants([]);
      }
    }
  }, [open, initialVisibility]);

  const handleVisibilityChange = (data: VisibilityData) => {
    setVisibility(data.visibility);
    setVisibleToPlans(data.visibleToPlans);
    setVisibleToTenants(data.visibleToTenants);
  };

  const handleConfirm = () => {
    onConfirm({
      visibility,
      visibleToPlans,
      visibleToTenants,
    });
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  // Validate that restricted visibility has at least one selection
  const isValidSelection = () => {
    if (visibility === 'plans' && visibleToPlans.length === 0) {
      return false;
    }
    if (visibility === 'tenants' && visibleToTenants.length === 0) {
      return false;
    }
    return true;
  };

  const getValidationMessage = () => {
    if (visibility === 'plans' && visibleToPlans.length === 0) {
      return 'Please select at least one plan';
    }
    if (visibility === 'tenants' && visibleToTenants.length === 0) {
      return 'Please select at least one tenant';
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-blue-500" />
            Set Template Visibility
          </DialogTitle>
          <DialogDescription>
            Choose who can see and use{' '}
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              "{templateName}"
            </span>{' '}
            in their {templateType} template gallery.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <VisibilityControlsPanel
            visibility={visibility}
            visibleToPlans={visibleToPlans}
            visibleToTenants={visibleToTenants}
            onChange={handleVisibilityChange}
          />

          {/* Validation message */}
          {!isValidSelection() && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {getValidationMessage()}
              </p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValidSelection() || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Template'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
